import React, { FormEvent, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';

import { CREDIT_TYPES, VOUCHER_TO_CREDIT_CREDIT_TYPES } from '~/consts/credits';
import { ROLE_TOUR_COORDINATOR } from '~/consts/roles';

import { useCreateCredit } from '~/hooks/apis/payments/useCreateCredit';
import { useCheckVoucherToCredit } from '~/hooks/apis/promo/useCheckVoucherToCredit';
import { useExpireVoucherUser } from '~/hooks/apis/promo/useExpireVoucherUser';
import { useGetUser } from '~/hooks/apis/users/useGetUser';
import useCurrentUser from '~/hooks/useCurrentUser';

import { isAdmin } from '~/utils/adminPermission';
import { getDefaultCreditExpiry, isTicketIdRequiredForCreditType } from '~/utils/creditDefaults';
import currencyFormatter from '~/utils/currencyFormatter';
import { deriveCustomerCurrency } from '~/utils/customOfferFormUtils';
import { getVoucherToCreditTransactionKey } from '~/utils/voucherToCredit';

const CREDIT_TYPE_OPTIONS = CREDIT_TYPES.filter(({ creditType }) =>
  VOUCHER_TO_CREDIT_CREDIT_TYPES.includes(creditType),
).sort((a, b) => (a.label || a.creditType).localeCompare(b.label || b.creditType));

const CURRENCY_OPTIONS = [
  ...new Set(
    libRegions
      .getRegions()
      .map((region) => region.currencyCode)
      .filter(Boolean),
  ),
].sort();

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * Called as soon as credit has been granted, even if removing the voucher then fails,
   * so the caller can stop the same assignment from being converted again.
   */
  onCreditAdded: (voucherUserId: string) => void;
  /**
   * Called when the voucher users list is out of date and should be refreshed: once credit has been granted
   * and the voucher removed, or when the pre-submit check finds the assignment can no longer be converted.
   */
  onVoucherUsersChanged: () => void;
  voucherUserId: string;
  voucherId: string;
  userId: string;
  brand: App.Brands;
  userLabel: string;
  voucherName: string;
  /** The voucher reward's currency, if it has one. Otherwise the customer's own currency is used. */
  voucherCurrency?: string;
};

function getErrorMessage(error: unknown): string {
  const apiError = error as { errors?: Array<string>; message?: string };
  return apiError?.errors?.[0] || apiError?.message || 'Unknown error';
}

export default function VoucherToCreditDialog({
  open,
  onClose,
  onCreditAdded,
  onVoucherUsersChanged,
  voucherUserId,
  voucherId,
  userId,
  brand,
  userLabel,
  voucherName,
  voucherCurrency,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();

  const createCreditMutation = useCreateCredit();
  const expireVoucherUserMutation = useExpireVoucherUser();
  const checkVoucherToCredit = useCheckVoucherToCredit({ voucherUserId, voucherId, userId, brand });
  const [isCheckingVoucher, setIsCheckingVoucher] = useState(false);

  const [amount, setAmount] = useState('');
  // Only needed when the voucher has no currency of its own.
  const { data: customer, isLoading: isCustomerLoading } = useGetUser({
    userId,
    brand,
    enabled: !voucherCurrency,
  });
  const suggestedCurrency =
    voucherCurrency ??
    (customer?.country_code ? deriveCustomerCurrency(brand, customer.country_code) : null) ??
    libRegions.getDefaultRegion(brand)?.currencyCode ??
    '';
  // Wait for the customer's currency before submitting, so we never fall back to the wrong one by accident.
  const isCurrencyLoading = !voucherCurrency && isCustomerLoading;
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const currency = selectedCurrency ?? suggestedCurrency;
  const [creditType, setCreditType] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => getDefaultCreditExpiry());
  const [ticketId, setTicketId] = useState('');
  const [comment, setComment] = useState('');
  // Set once credit is granted but removing the voucher failed. From then on the dialog only retries the removal.
  const [creditAddedMessage, setCreditAddedMessage] = useState<string | null>(null);
  // isPending only updates on the next render, so two quick submits (e.g. Enter twice) could both get through.
  const submitInFlightRef = useRef(false);

  // Same rule as the user Credits page: only admins and tour coordinators may move the expiry date.
  const canEditExpiry = isAdmin(currentUser) || Boolean(currentUser?.roles?.includes(ROLE_TOUR_COORDINATOR));
  const isTicketIdRequired = isTicketIdRequiredForCreditType(creditType);
  const isSubmitting = isCheckingVoucher || createCreditMutation.isPending || expireVoucherUserMutation.isPending;
  const amountNumber = Number(amount);
  const isAmountValid = amount !== '' && Number.isFinite(amountNumber) && amountNumber > 0;

  const currencyOptions = useMemo(
    () =>
      !suggestedCurrency || CURRENCY_OPTIONS.includes(suggestedCurrency)
        ? CURRENCY_OPTIONS
        : [suggestedCurrency, ...CURRENCY_OPTIONS],
    [suggestedCurrency],
  );

  const handleCreditTypeChange = (value: string) => {
    setCreditType(value);
    setExpiresAt(getDefaultCreditExpiry(value));
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const removeVoucher = async (formattedAmount: string) => {
    try {
      await expireVoucherUserMutation.mutateAsync(voucherUserId);
    } catch (error) {
      // Credit is already granted, so keep the dialog open and only offer to retry the removal.
      // Closing here would leave the Convert button usable and the customer could be credited twice.
      setCreditAddedMessage(
        `${formattedAmount} credit was added, but the voucher was not removed: ${getErrorMessage(error)}`,
      );
      return;
    }

    enqueueSnackbar(`${formattedAmount} credit added and voucher removed`, { variant: 'success' });
    onVoucherUsersChanged();
    onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (submitInFlightRef.current || isSubmitting) {
      return;
    }

    const formattedAmount = `${currencyFormatter(currency, amountNumber, 2)} ${currency}`;

    submitInFlightRef.current = true;
    try {
      if (creditAddedMessage) {
        await removeVoucher(formattedAmount);
        return;
      }

      if (!isAmountValid || !currency || !creditType || (isTicketIdRequired && !ticketId.trim())) {
        return;
      }

      // Keep a trace of the voucher on the credit so finance can see why it was granted.
      const comments = [
        comment.trim(),
        `Voucher to credit: ${voucherName} (assignment ${voucherUserId})`,
        isTicketIdRequired ? `Ticket ID: ${ticketId.trim()}` : '',
      ]
        .filter(Boolean)
        .join(' - ');

      // The list the agent clicked from may be stale: the customer may have used the voucher since,
      // or another agent may have removed or converted it. Re-check right before any money moves.
      setIsCheckingVoucher(true);
      let blockReason: string | null;
      try {
        blockReason = await checkVoucherToCredit();
      } catch (error) {
        enqueueSnackbar(`Could not check the voucher, no credit was added: ${getErrorMessage(error)}`, {
          variant: 'error',
        });
        return;
      } finally {
        setIsCheckingVoucher(false);
      }
      if (blockReason) {
        enqueueSnackbar(`${blockReason}, no credit was added`, { variant: 'error' });
        onVoucherUsersChanged();
        onClose();
        return;
      }

      // Grant credit first: if this fails nothing has changed and the agent can retry.
      // The transaction key is fixed per assignment, so a retry after an unclear failure (e.g. a timeout
      // after payments already granted it), a reload or a second tab can never grant the credit twice.
      let grantedAmount = formattedAmount;
      try {
        const response = await createCreditMutation.mutateAsync({
          amount,
          currency,
          fk_member: userId,
          credit_type: creditType,
          comments,
          expires_at: expiresAt,
          brand,
          transaction_key: getVoucherToCreditTransactionKey(voucherUserId),
        });
        // If this assignment was already credited, payments returns that credit, so show its real amount.
        const granted = response?.result;
        if (granted?.amount != null && granted.currency) {
          grantedAmount = `${currencyFormatter(granted.currency, Number(granted.amount), 2)} ${granted.currency}`;
        }
      } catch (error) {
        enqueueSnackbar(`Error adding credit: ${getErrorMessage(error)}`, { variant: 'error' });
        return;
      }

      onCreditAdded(voucherUserId);
      await removeVoucher(grantedAmount);
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="voucher-to-credit-title">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id="voucher-to-credit-title">Convert voucher to credit</DialogTitle>
        <DialogContent>
          {creditAddedMessage ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {creditAddedMessage}. Do not add the credit again. Retry removing the voucher, or delete it manually.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                This adds credit to <strong>{userLabel}</strong> and removes voucher <strong>{voucherName}</strong> from
                their account.
              </Alert>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                  error={amount !== '' && !isAmountValid}
                  helperText={amount !== '' && !isAmountValid ? 'Enter an amount greater than 0' : ' '}
                  required
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Currency"
                  value={currency}
                  onChange={(event) => setSelectedCurrency(event.target.value)}
                  helperText=" "
                  select
                  required
                  fullWidth
                >
                  {currencyOptions.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TextField
                label="Credit type"
                value={creditType}
                onChange={(event) => handleCreditTypeChange(event.target.value)}
                select
                required
                fullWidth
              >
                {CREDIT_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.creditType} value={item.creditType}>
                    {item.label || item.creditType}
                  </MenuItem>
                ))}
              </TextField>

              <DateTimeWidget
                label="Credit expiry"
                value={expiresAt}
                onChange={setExpiresAt}
                disabled={!canEditExpiry}
                helperText={canEditExpiry ? undefined : 'Only admins can change the credit expiry'}
                disablePast
                fullWidth
              />

              {isTicketIdRequired && (
                <TextField
                  label="Ticket ID"
                  value={ticketId}
                  onChange={(event) => setTicketId(event.target.value)}
                  required
                  fullWidth
                />
              )}

              <TextField
                label="Comment"
                placeholder="Why this voucher is being converted to credit"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                multiline
                fullWidth
              />

              <Typography variant="body2" color="text.secondary">
                The voucher name and assignment ID are added to the credit comment automatically.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={
              isSubmitting ||
              (!creditAddedMessage &&
                (isCurrencyLoading ||
                  !currency ||
                  !isAmountValid ||
                  !creditType ||
                  (isTicketIdRequired && !ticketId.trim())))
            }
          >
            {creditAddedMessage
              ? isSubmitting
                ? 'Removing voucher…'
                : 'Retry removing voucher'
              : isSubmitting
                ? 'Completing…'
                : 'Complete'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
